import {
	Button,
	Flex,
	Input,
	InputGroup,
	InputLeftElement,
	Radio,
	RadioGroup,
	Stack,
	Text,
	useDisclosure,
	useToast
} from '@chakra-ui/react';
import DashLayout from '../../components/DashLayout';
import { PageHeader } from '../../components/PageHeader';
import { Loading } from '../../components/Loading';
import { EmptyState } from '../../components/EmptyState';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { useCallback, useEffect, useState } from 'react';
import { listarBooks } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import ButtonOptions from '../../components/ButtonOptions';
import { DrawerCreate } from './DrawerCreate';
import { DrawerUpdate } from './DrawerUpdate';
import { VscOpenPreview } from 'react-icons/vsc';

export type BookGet = {
	id: string;
	nome: string;
	descricao: string;
	capa: string;
	idioma: string;
	nivel: string;
};

const bookDefault: BookGet = {
	id: '',
	nome: '',
	descricao: '',
	capa: '',
	idioma: '',
	nivel: ''
};

export const Books = () => {
	const [ books, setBooks ] = useState<BookGet[]>([]);
	const [ loading, setLoading ] = useState(true);
	const [ search, setSearch ] = useState<string>('');
	const [ filter, setFilter ] = useState<string>('Todos');
	const [ bookSelected, setBookSelected ] = useState<BookGet>(bookDefault);

	const toast = useToast();
	const navigate = useNavigate();
	const createDisclosure = useDisclosure();
	const updateDisclosure = useDisclosure();

	const buscarBooks = useCallback(
		() => {
			setLoading(true);
			listarBooks()
				.then((response) => {
					setBooks(response.data);
					setLoading(false);
				})
				.catch((err) => {
					toast({
						title: `Não foi possível carregar o livro${err}`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(() => {
		buscarBooks();
	}, [ buscarBooks ]);

	const handleBookEdit = (book: BookGet) => {
		setBookSelected(book);
		updateDisclosure.onOpen();
	};

	const abrirLessons = (book: BookGet) => {
		navigate(`/dashboard/${book.id}/lessons`);
	};

	// A lista exibida e derivada de `books` + `filter` a cada render. Guardar o resultado em um
	// state separado deixava o filtro escolhido durante o fetch ser sobrescrito pela resposta.
	const booksViwed = filter === 'Todos' ? books : books.filter((b) => b.idioma === filter);

	return (
		<DashLayout>
			<Flex width="100%" height="100%" flexDir="column">
				<PageHeader title="Books">
					<Flex gap="20px" flexWrap="wrap">
						<InputGroup gap={'20px'}>
							<InputLeftElement pointerEvents="none" color={'gray.600'}>
								<FaSearch />
							</InputLeftElement>
							<Input
								type="tel"
								placeholder="Buscar books..."
								onChange={(e) => setSearch(e.target.value)}
								value={search}
								borderColor="mist"
								bgColor="paper"
							/>
							<Button
								leftIcon={<FaPlus />}
								colorScheme="blue"
								variant="solid"
								onClick={() => createDisclosure.onOpen()}
							>
								Novo
							</Button>
						</InputGroup>
					</Flex>
					<RadioGroup onChange={setFilter} value={filter}>
						<Stack direction="row" paddingTop="15px" justifyContent="center">
							<Radio value="Todos">Todos</Radio>
							<Radio value="Inglês">Inglês</Radio>
							<Radio value="Português">Português</Radio>
							<Radio value="Espanhol">Espanhol</Radio>
							<Radio value="Italiano">Italiano</Radio>
							<Radio value="Francês">Francês</Radio>
						</Stack>
					</RadioGroup>
				</PageHeader>
				<Flex
					height="90%"
					gap="20px"
					justifyContent="center"
					paddingTop="20px"
					flexWrap="wrap"
					overflowY="auto"
				>
					{!loading ? (
						<>
							{
							booksViwed.length > 0 ? 
							booksViwed.map((book) => {
							if (
								!Object.values(book)
									.map((variavel) => variavel)
									.reduce((a, b) => (b = a + ' ' + b))
									.toLowerCase()
									.includes(search.toLowerCase())
							)
								return null;

							return (
									<Flex
										key={book.id}
										width="250px"
										height="210px"
										bgColor="blue.700"
										borderRadius="25px"
										alignItems="center"
										justifyContent="center"
										flexDir="column"
										gap="10px"
										borderWidth="3px"
										borderColor="white"
										boxShadow="lg"
										role="button"
										tabIndex={0}
										aria-label={`Abrir as lessons do book ${book.nome}`}
										_hover={{
											backgroundColor: 'blue.800',
											cursor: 'pointer'
										}}
										_focusVisible={{
											outline: '3px solid',
											outlineColor: 'brass',
											outlineOffset: '2px'
										}}
										onClick={() => abrirLessons(book)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												abrirLessons(book);
											}
										}}
									>
										<Flex
											height="130px"
											bgImage={book.capa}
											minWidth="230px"
											maxWidth="230px"
											bgPosition="center"
											bgSize="cover"
											borderRadius="10px"
										/>
										<Flex alignItems="center" gap="10px" justifyContent="center">
											<Flex flexDir="column">
												<Text color="white" fontSize="20px" fontWeight="800" textAlign="center">
													{book.nome}
												</Text>
											</Flex>
											<Flex
												// O card inteiro abre as lessons, entao nada do menu pode
												// subir o clique e navegar por baixo do popover.
												onClick={(e) => e.stopPropagation()}
												onKeyDown={(e) => e.stopPropagation()}
											>
												<ButtonOptions isBook={true} onEdit={() => handleBookEdit(book)} />
											</Flex>
										</Flex>
									</Flex>
								);
						})
						:
							<EmptyState
								icon={VscOpenPreview}
								title="Nenhum book encontrado"
								description="Cadastre um novo book ou ajuste o filtro de idioma."
								actionLabel="Novo book"
								onAction={() => createDisclosure.onOpen()}
							/>
						}
						</>
					) : (
						<Loading />
					)}
				</Flex>
			</Flex>
			<DrawerCreate
				isOpen={createDisclosure.isOpen}
				onClose={createDisclosure.onClose}
				onOpen={createDisclosure.onOpen}
				afterClose={() => buscarBooks()}
			/>
			<DrawerUpdate
				isOpen={updateDisclosure.isOpen}
				onClose={updateDisclosure.onClose}
				onOpen={updateDisclosure.onOpen}
				afterClose={buscarBooks}
				bookUpdate={bookSelected}
			/>
		</DashLayout>
	);
};
